import { useQuery, QueryResult, QueryHookOptions, OperationVariables } from '@apollo/client'
import { DocumentNode } from 'graphql'
import { useState, useEffect } from 'react'

type PollingQueryProps<TData, TVariables extends OperationVariables> = QueryHookOptions<TData, TVariables> & {
  query: DocumentNode
  children: (res: QueryResult<TData, TVariables>) => JSX.Element
}

/**
 * PollingQuery is a drop-in replacement component for the default react-apollo Query component.
 *
 * Necessary because of the way nested query components misbehave in the react-apollo implementation.
 *
 * Overrides whatever `pollInterval` you pass in to equal undefined
 *
 * Important! polls once every `pollMs`!
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export const PollingQuery = <TData = any, TVariables extends OperationVariables = {}>(
  props: PollingQueryProps<TData, TVariables>
) => {
  const [pollId] = useState(nextPollId++)
  const { query } = props

  const res = useQuery<TData, TVariables>(query, { ...props, pollInterval: undefined })

  useEffect(
    () => () => {
      unregisterPoll(pollId)
    },
    []
  )

  useEffect(() => {
    justFetched(pollId, res.refetch)
  }, [res])

  return props.children(res)
}

/**
 * Book-keeping data structure to keep track of all active instances of PollingQuery
 */
interface PollComp {
  refetch: () => Promise<any>
  canRefetchAfter?: Date
}

// Global state
let nextPollId = 1
const pollingComponents: { [id: number]: PollComp } = {}
let lastRefetchMs = new Date().getTime()
const pollMs = 6000 // every 6 seconds

function justFetched(id: number, refetch: () => Promise<any>) {
  const canRefetchAfter = soon()
  pollingComponents[id] = { refetch, canRefetchAfter }
}

function unregisterPoll(id: number) {
  delete pollingComponents[id]
}

// Poll every pollMs as long as the window's showing (requestAnimationFrame)
function frame() {
  try {
    maybeRefetchAll()
  } catch (e) {
    console.error(e)
  }
  window.requestAnimationFrame(frame)
}

function maybeRefetchAll() {
  const nowMs = new Date().getTime()
  if (nowMs - lastRefetchMs > pollMs) {
    refetchAllEligibleQueries(nowMs)
    lastRefetchMs = nowMs
  }
}

function refetchAllEligibleQueries(nowMs: number) {
  const pollsToRefetch = Object.values(pollingComponents).filter(
    poll => poll.canRefetchAfter && nowMs >= poll.canRefetchAfter.getTime()
  )
  if (pollsToRefetch.length === 0) {
    return
  }
  console.log('Refetching %d eligible queries...', pollsToRefetch.length)
  for (const poll of pollsToRefetch) {
    // Don't refetch while the request is in flight
    poll.canRefetchAfter = undefined
    poll
      .refetch()
      .then(() => {
        poll.canRefetchAfter = soon()
      })
      .catch(err => {
        console.error(err)
        poll.canRefetchAfter = soon()
      })
  }
}

function soon() {
  return new Date(new Date().getTime() + pollMs / 2)
}

// Kick off polling
frame()

// Check at a longer interval in background tabs
window.setInterval(maybeRefetchAll, pollMs * 4)
